"use client";

import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Link from "next/link";
import { useI18n } from "./i18n-provider";
import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import Image from "next/image";
import { trackEvent } from "@/lib/analytics";
import { getDefaultPostLoginPath } from "@/lib/auth/post-login-path";
import {
  OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS,
  OAUTH_NEXT_COOKIE_NAME,
} from "@/lib/auth/oauth-constants";

type LoginFormProps = React.ComponentPropsWithoutRef<"div"> & {
  redirectTo?: string;
};

export function LoginForm({ className, redirectTo, ...props }: LoginFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { locale, dictionary } = useI18n();
  const t = dictionary.authForms.login;
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    trackEvent("view_signin", {
      locale,
      ...(redirectTo ? { redirect: redirectTo } : {}),
    });
    // Fire once per mount; locale/redirect are stable for a page view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleButtonSrc =
    resolvedTheme === "dark"
      ? "/google/dark/web_dark_rd_SI.svg"
      : "/google/light/web_light_rd_SI.svg";

  const handleGoogleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!isGoogleLoading) {
        handleGoogleSignIn();
      }
    }
  };

  const handleGoogleSignIn = async () => {
    const supabase = createClient();
    setIsGoogleLoading(true);
    setError(null);

    try {
      const next = redirectTo ?? getDefaultPostLoginPath(locale);
      document.cookie = `${OAUTH_NEXT_COOKIE_NAME}=${encodeURIComponent(next)}; path=/; max-age=${OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
      trackEvent("oauth_start", { source: "signin", locale });
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/oauth?next=${encodeURIComponent(next)}`,
        },
      });

      if (error) {
        throw error;
      }
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t.title}</CardTitle>
          <CardDescription>{t.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-6 items-center">
            {error && <p className="text-sm text-brand-primary">{error}</p>}
            {isGoogleLoading ? (
              <span className="text-sm text-center text-muted-foreground">
                {t.connecting}
              </span>
            ) : mounted ? (
              <Image
                src={googleButtonSrc}
                alt="Sign in with Google"
                width={175}
                height={40}
                className="cursor-pointer"
                onClick={handleGoogleSignIn}
              />
            ) : (
              <div style={{ width: 175, height: 40 }} />
            )}
          </div>
          <div className="mt-4 text-center text-sm">
            {t.dontHaveAccount}{" "}
            <Link
              href={
                redirectTo
                  ? `/${locale}/sign-up?redirect=${encodeURIComponent(redirectTo)}`
                  : `/${locale}/sign-up`
              }
              className="underline underline-offset-4"
            >
              {t.signUpLink}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
